/**
 * Variant-B trade market. Entered from either the runner HUD (mid-session,
 * pauses RunnerScene) or the Launcher tile (between sessions). Hero walks
 * freely L/R + jumps inside a 1-screen stone room; a trade-board fixture on
 * the right wall shows 5 cards from the rolling offer buffer with a Up/Down
 * cursor. Y/K accepts the highlighted offer; Esc / left-portal exits back
 * to the caller.
 *
 * Wallet source switches by entry point:
 *   - "runner" → RunnerScene exposes its RunnerWallet through the registry
 *     before pausing; reads/writes route through the scene's existing state.
 *   - "launcher" → uses a persistent LauncherWallet snapshot stored in the
 *     registry. Trades on the launcher side are not yet propagated into a
 *     SessionConfig handoff to the runner (see TODO note in the design doc).
 */

import Phaser from "phaser";

import { GAMEPLAY, NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import type { AuthorIdentity, OfferAffordabilityTier, TradeOffer } from "../types";

import { KNIGHT_IDLE_ANIM, KNIGHT_IDLE_KEY, KNIGHT_RUN_ANIM } from "./BootScene";
import { AudioService } from "../services/AudioService";
import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { InputService } from "../services/InputService";
import { OFFER_LIFETIME_MS, ShopService } from "../services/ShopService";
import { ProfileTooltipService } from "../services/ProfileTooltipService";
import {
  LauncherWallet,
  makeDefaultWalletSnapshot,
  offerIsAffordable,
  resourcesToDelta,
  type Wallet,
  type WalletSnapshot,
} from "../services/Wallet";

import { AuthorBadge } from "../ui/AuthorBadge";
import { OFFER_CARD_HEIGHT, OFFER_CARD_WIDTH, OfferCard } from "../ui/OfferCard";
import { WalletHud } from "../ui/WalletHud";
import { t } from "../i18n";

const FLOOR_Y = NATIVE_HEIGHT - 28;
const EXIT_PORTAL_X = 12;
/**
 * Cards stack vertically in the middle of the room and form the backdrop;
 * each card has a slim platform directly beneath it that the hero can jump
 * onto. Standing on platform N selects card N. The hero renders at a higher
 * depth than the cards so they read as walking in front of the stack.
 *
 * Geometry is tuned so single-jump (≈57 px arc at GAMEPLAY.gravity) covers
 * the gap from one platform to the next, and from the floor to the bottom
 * platform.
 */
const CARD_STACK_WIDTH = 200; // = OFFER_CARD_WIDTH (kept independent for layout math)
const CARD_STACK_X = Math.round((NATIVE_WIDTH - CARD_STACK_WIDTH) / 2);
const PLATFORM_HEIGHT = 4;
const ROW_HEIGHT = 44; // card (36) + gap (4) + platform (4) per row
const STACK_TOP_Y = 42; // y of the topmost card — clears the shared WalletHud (y=4..32) and leaves room for the scroll up-arrow
const DEPTH_CARDS = 1;
const DEPTH_PLATFORMS = 2;
const DEPTH_HERO = 10;
const DEPTH_HUD = 20;

export interface ShopSceneInit {
  /** Phaser scene key to return to on exit ("runner" or "launcher"). */
  returnTo: "runner" | "launcher";
  walletSource: "runner" | "launcher";
}

export class ShopScene extends Phaser.Scene {
  static readonly KEY = "ShopScene";

  private returnTo: ShopSceneInit["returnTo"] = "launcher";
  private walletSource: ShopSceneInit["walletSource"] = "launcher";
  private enteredAtMs = 0;

  private input2!: InputService;
  private shop!: ShopService;
  private identity!: AuthorIdentityService;
  private wallet!: Wallet;
  private audio!: AudioService;
  /** Tracks the topmost offer id so we only animate-arrive truly new
   *  arrivals, not buffer reshuffles after a trade/expiry. */
  private unsubNewOffer?: () => void;
  private telemetry!: {
    emit: (layer: 1 | 2 | 3, tag: string, data?: Record<string, number | string | boolean>) => void;
  };

  private hero!: Phaser.Physics.Arcade.Sprite;
  private cards: OfferCard[] = [];
  private scrollOffset = 0;
  private unsubShop?: () => void;
  private exitGlow?: Phaser.GameObjects.Rectangle;
  /** Platform sprites — index 0 = top of stack (newest visible offer),
   *  last index = bottom (oldest visible offer / closest to floor). */
  private platforms: Phaser.GameObjects.Rectangle[] = [];
  private platformGroup!: Phaser.Physics.Arcade.StaticGroup;
  /** Edge-trigger guard for Up/Down scroll input. */
  private prevScrollDir = 0;
  /** True while a scroll-slide tween is in flight. Ignores further scroll
   *  presses during the animation so cards don't pile up mid-motion. */
  private animatingScroll = false;
  /** Time (ms) until platform drop-through input lapses. While `>now`, the
   *  collider's process callback skips contact so the hero falls off. */
  private dropThroughUntilMs = 0;
  /** Time (ms) when the player last initiated a drop-through. Used as a
   *  short cooldown so a held Down arrow doesn't re-arm the window every
   *  frame after the hero clears the platform. */
  private dropThroughArmedAtMs = -Infinity;
  /** Scroll indicator widgets — vertical track on the right wall with
   *  up/down triangles + a knob showing where the visible window sits
   *  inside the full 20-offer buffer. */
  private scrollUpArrow!: Phaser.GameObjects.Triangle;
  private scrollDownArrow!: Phaser.GameObjects.Triangle;
  private scrollTrack!: Phaser.GameObjects.Rectangle;
  private scrollCursor!: Phaser.GameObjects.Rectangle;
  private scrollLabel!: Phaser.GameObjects.Text;
  /** Out-of-view arrival hint — appears next to the up-arrow when a new
   *  offer pushes onto buffer[0] while the player is scrolled away. */
  private newOfferHint!: Phaser.GameObjects.Text;
  /** Fictive "N players online" counter — flavour text. Drifts ±1 between
   *  71 and 77 every ~10–20 s. */
  private playersOnline = 74;
  private playersOnlineText!: Phaser.GameObjects.Text;
  /** Shared wallet HUD — identical layout to the runner's HUD so the
   *  player can see what they have to spend without leaving the market. */
  private walletHud!: WalletHud;
  /** Action prompt floating above the hero — shows "[Y] TRADE" on an
   *  affordable offer or a missing-resource hint on an unaffordable one. */
  private actionPrompt!: Phaser.GameObjects.Container;
  private actionPromptBg!: Phaser.GameObjects.Rectangle;
  private actionPromptText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: ShopScene.KEY, physics: { arcade: { gravity: { x: 0, y: GAMEPLAY.gravity } } } });
  }

  init(data: Partial<ShopSceneInit>): void {
    this.returnTo = data.returnTo ?? "launcher";
    this.walletSource = data.walletSource ?? "launcher";
  }

  create(): void {
    this.enteredAtMs = this.time.now;
    this.input2 = new InputService(this);
    this.audio = new AudioService(this);
    // Audio context starts suspended until first user gesture.
    this.input.keyboard?.once("keydown", () => this.audio.resume());
    this.input.once("pointerdown", () => this.audio.resume());

    // Resolve services from the registry (set by BootScene / scenes earlier).
    this.identity = this.registry.get("authorIdentityService") as AuthorIdentityService;
    if (!this.identity) {
      this.identity = new AuthorIdentityService();
      this.registry.set("authorIdentityService", this.identity);
    }
    const telemetry = this.registry.get("telemetryService") as
      | { emit: (layer: 1 | 2 | 3, tag: string, data?: Record<string, number | string | boolean>) => void }
      | undefined;
    this.telemetry = telemetry ?? { emit: () => undefined };

    this.shop = ShopService.getOrCreate({
      game: this.game,
      identity: this.identity,
      getCurrentWallet: () => this.wallet,
      emitTelemetry: (l, t, d) => this.telemetry.emit(l, t, d),
      now: () => this.time.now,
    });

    this.wallet = this.resolveWallet();

    this.buildRoom();
    this.spawnHero();
    this.buildTradeBoard();
    this.buildScrollIndicator();
    this.buildPlayersOnline();
    this.buildWalletHud();
    this.buildActionPrompt();
    this.renderCards();
    this.updateWalletHud();

    this.unsubShop = this.shop.subscribe(() => this.renderCards());
    this.unsubNewOffer = this.shop.onNewOffer(() => {
      // The render call from subscribe() runs first; this fires after
      // and gives us the chance to animate the freshly placed top card.
      this.time.delayedCall(0, () => {
        this.audio.shopOfferArrived();
        const standingOnOffer = this.selectedOfferIndex() >= 0;
        if (!standingOnOffer && this.scrollOffset !== 0) {
          // Auto-scroll to top with a curtain sweep (old cards drop off
          // the bottom, new cards drop in from above). Single visual
          // animation regardless of how far we're scrolling.
          this.sweepToOffset(0);
          return;
        }
        if (this.scrollOffset === 0) {
          this.cards[0]?.animateArrive();
        } else {
          this.maybeFlashOutOfViewHint();
        }
      });
    });

    this.telemetry.emit(1, "ugc.shop.enter", {
      returnTo: this.returnTo,
      walletSource: this.walletSource,
    });
  }

  override update(_t: number, dt: number): void {
    const input = this.input2.read();
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;

    // Exit — Esc / Start anywhere closes the shop.
    if (input.pausePressed) {
      this.exit();
      return;
    }

    // Accept — Y / K / gamepad-Y. The "selected" offer is the one whose
    // platform the hero is currently standing on; pressing Y on the floor
    // surfaces a one-line hint banner.
    // Buy on either interact (Y / K / gamepad Y) or attack (J / gamepad X).
    // The attack key is otherwise unused in the market, and mapping it to
    // "buy" makes the runner's combat hand also work as the trade hand.
    if (input.interactPressed || input.attackPressed) {
      this.tryAcceptStandingOffer();
    }

    // Up/Down scrolls the 20-offer buffer, EXCEPT when Down is the
    // drop-through input (hero grounded on a platform → falling through
    // takes priority over scrolling).
    const onPlatform = this.selectedOfferIndex() >= 0;
    const downIsDropThrough = input.moveY > 0.5 && onPlatform;
    if (!downIsDropThrough) {
      this.maybeScrollBuffer(input);
    } else {
      // Reset the edge-trigger so a subsequent Down on the floor still scrolls.
      this.prevScrollDir = 0;
    }

    // Hero movement (free walk + jump).
    const speed = GAMEPLAY.shopWalkVelocity;
    if (body) {
      body.setVelocityX(input.moveX * speed);
      if (input.moveX < -0.1) this.hero.setFlipX(true);
      else if (input.moveX > 0.1) this.hero.setFlipX(false);

      if (input.jumpPressed && body.blocked.down) {
        // Jumping from the topmost visible platform when there are hidden
        // offers above scrolls the buffer up by one with the same smooth
        // slide the Up arrow uses. Saves a trip back to the floor.
        const sel = this.selectedOfferIndex();
        if (
          sel === this.scrollOffset &&
          this.scrollOffset > 0 &&
          !this.animatingScroll
        ) {
          this.slideScrollAnimated(-1, this.scrollOffset - 1);
          this.audio.shopScroll();
          this.telemetry.emit(1, "ugc.shop.scroll", {
            direction: "up",
            scrollOffset: this.scrollOffset,
            source: "jump",
          });
        }
        body.setVelocityY(-GAMEPLAY.jumpVelocity);
        this.audio.jump();
      }

      // Drop-through / scroll-down: holding Down while standing on the
      // BOTTOM visible platform scrolls the buffer down by one (older
      // offer slides into the bottom slot) when there are more offers
      // below. Only when the player is already at the oldest visible
      // offer does the drop-through fall back to letting them fall to
      // the floor.
      const nowMs = this.time.now;
      if (
        input.moveY > 0.5 &&
        body.blocked.down &&
        nowMs - this.dropThroughArmedAtMs > 260
      ) {
        const sel = this.selectedOfferIndex();
        const lastSlotOfferIdx =
          this.scrollOffset + GAMEPLAY.shopBoardVisibleCards - 1;
        const maxOffset = Math.max(
          0,
          this.shop.offers.length - GAMEPLAY.shopBoardVisibleCards,
        );
        const onBottomVisible = sel === lastSlotOfferIdx && sel >= 0;
        const canScrollDown = this.scrollOffset < maxOffset;
        if (onBottomVisible && canScrollDown && !this.animatingScroll) {
          this.slideScrollAnimated(1, this.scrollOffset + 1);
          this.audio.shopScroll();
          this.telemetry.emit(1, "ugc.shop.scroll", {
            direction: "down",
            scrollOffset: this.scrollOffset,
            source: "drop",
          });
          this.dropThroughArmedAtMs = nowMs;
        } else {
          this.dropThroughUntilMs = nowMs + 220;
          this.dropThroughArmedAtMs = nowMs;
        }
      }
    }

    // Animations.
    if (Math.abs(body?.velocity.x ?? 0) > 5) {
      if (this.hero.anims.currentAnim?.key !== KNIGHT_RUN_ANIM) this.hero.play(KNIGHT_RUN_ANIM);
    } else if (this.hero.anims.currentAnim?.key !== KNIGHT_IDLE_ANIM) {
      this.hero.play(KNIGHT_IDLE_ANIM);
    }

    // Highlight whichever offer the hero is standing on + refresh action prompt.
    this.refreshSelectionHighlight();
    this.updateActionPrompt();
    this.updateWalletHud();
    this.updateCountdowns();

    // Exit portal — generous proximity zone since the world bounds clamp
    // the hero body slightly before sprite.x reaches the portal's true x.
    const inGlowZone = this.hero.x <= EXIT_PORTAL_X + 28;
    if (this.exitGlow) this.exitGlow.setAlpha(inGlowZone ? 0.9 : 0.4);
    if (inGlowZone) {
      const atLeftWall = body?.blocked.left === true;
      if (atLeftWall || this.hero.x <= EXIT_PORTAL_X + 6) {
        this.exit();
        return;
      }
    }

    void dt;
  }

  /**
   * Up/Down arrows or D-pad scrolls the visible window through the 20-offer
   * buffer. Edge-triggered so a held key advances one step at a time.
   * Only fires when the hero is NOT in the exit glow zone (Up there would
   * be ambiguous with "step into portal").
   */
  private maybeScrollBuffer(input: { moveY: number }): void {
    const offers = this.shop.offers;
    if (offers.length === 0) return;
    const dir = input.moveY < -0.5 ? -1 : input.moveY > 0.5 ? 1 : 0;
    if (dir === this.prevScrollDir) return;
    this.prevScrollDir = dir;
    if (dir === 0) return;
    if (this.animatingScroll) return;
    const maxOffset = Math.max(0, offers.length - GAMEPLAY.shopBoardVisibleCards);
    const next = Phaser.Math.Clamp(this.scrollOffset + dir, 0, maxOffset);
    if (next === this.scrollOffset) return;
    this.slideScrollAnimated(dir, next);
    this.audio.shopScroll();
    this.telemetry.emit(1, "ugc.shop.scroll", {
      direction: dir === 1 ? "down" : "up",
      scrollOffset: next,
    });
  }

  /**
   * Smoothly slide the visible cards by one row. Existing cards tween by
   * ±ROW_HEIGHT (in pixel steps — the tween animates every intermediate
   * y); the exiting card destroys when it leaves the band, and a new card
   * spawns just off-screen on the opposite side and rides into its slot.
   * Falls back to an instant re-render only when there's no offer to
   * scroll INTO (edge of buffer).
   */
  private slideScrollAnimated(dir: 1 | -1, next: number): void {
    const offers = this.shop.offers;
    const visibleCount = GAMEPLAY.shopBoardVisibleCards;
    const enteringOfferIdx = dir === 1 ? next + visibleCount - 1 : next;
    const enteringOffer = offers[enteringOfferIdx];
    if (!enteringOffer) {
      // Nothing to slide into — just clamp and rebuild.
      this.scrollOffset = next;
      this.renderCards();
      return;
    }

    const yDelta = -dir * ROW_HEIGHT;
    const enteringSlot = dir === 1 ? visibleCount - 1 : 0;
    const enteringTargetY = STACK_TOP_Y + enteringSlot * ROW_HEIGHT;
    const enteringStartY = enteringTargetY - yDelta; // off-screen on the entering side

    const identity = this.identity.forSeed(enteringOffer.authorAvatarSeed);
    const newCard = new OfferCard(
      this,
      CARD_STACK_X,
      enteringStartY,
      enteringOffer,
      identity,
      this.identity,
    );
    newCard.container.setDepth(DEPTH_CARDS);
    newCard.setTier(this.tierFor(enteringOffer));

    const exitingSlotIdx = dir === 1 ? 0 : visibleCount - 1;
    const exitingCard = this.cards[exitingSlotIdx];

    // Commit the new scroll offset immediately so HUD widgets that read
    // scrollOffset (scroll cursor, label, action prompt) update without
    // waiting for the tween to finish.
    this.scrollOffset = next;
    this.updateScrollIndicator();

    this.animatingScroll = true;
    const targets = [...this.cards.map((c) => c.container), newCard.container];
    this.tweens.add({
      targets,
      y: `+=${yDelta}`,
      duration: 200,
      ease: "Cubic.easeOut",
      onComplete: () => {
        exitingCard?.destroy();
        const nextCards: OfferCard[] = [];
        if (dir === 1) {
          for (let i = 1; i < this.cards.length; i++) {
            if (this.cards[i] !== exitingCard) nextCards.push(this.cards[i]!);
          }
          nextCards.push(newCard);
        } else {
          nextCards.push(newCard);
          for (let i = 0; i < this.cards.length - 1; i++) {
            if (this.cards[i] !== exitingCard) nextCards.push(this.cards[i]!);
          }
        }
        this.cards = nextCards;
        this.animatingScroll = false;
        this.refreshSelectionHighlight();
        this.updateCountdowns();
      },
    });
  }

  /**
   * Multi-step "curtain sweep" to an arbitrary scrollOffset. Old cards
   * slide down off the bottom and fade; new cards drop in from above.
   * Used by the new-offer auto-scroll when the character isn't on a
   * platform so we don't chain N×slide animations or jump-cut between
   * positions. Single visual sweep regardless of distance scrolled.
   */
  private sweepToOffset(targetOffset: number): void {
    if (targetOffset === this.scrollOffset) return;
    if (this.animatingScroll) {
      // Mid-slide collision: snap to target rather than overlap tweens.
      this.scrollOffset = targetOffset;
      this.renderCards();
      return;
    }
    this.animatingScroll = true;
    const span = ROW_HEIGHT * GAMEPLAY.shopBoardVisibleCards;

    // Tween the outgoing cards down off the bottom and fade.
    const exitingCards = this.cards;
    if (exitingCards.length > 0) {
      this.tweens.add({
        targets: exitingCards.map((c) => c.container),
        y: `+=${span}`,
        alpha: 0,
        duration: 260,
        ease: "Cubic.easeIn",
        onComplete: () => {
          for (const c of exitingCards) c.destroy();
        },
      });
    }

    // Build the new card set at their final y positions, then nudge
    // them up by `span` so they start off-screen above and ride into
    // their slots when the tween below runs.
    this.scrollOffset = targetOffset;
    this.cards = [];
    const offers = this.shop.offers;
    for (let i = 0; i < GAMEPLAY.shopBoardVisibleCards; i++) {
      const offer = offers[targetOffset + i];
      if (!offer) continue;
      const finalY = STACK_TOP_Y + i * ROW_HEIGHT;
      const identity = this.identity.forSeed(offer.authorAvatarSeed);
      const card = new OfferCard(
        this,
        CARD_STACK_X,
        finalY - span,
        offer,
        identity,
        this.identity,
      );
      card.container.setDepth(DEPTH_CARDS);
      card.setTier(this.tierFor(offer));
      this.cards.push(card);
    }
    this.updateScrollIndicator();

    if (this.cards.length === 0) {
      this.animatingScroll = false;
      return;
    }
    this.tweens.add({
      targets: this.cards.map((c) => c.container),
      y: `+=${span}`,
      duration: 300,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.animatingScroll = false;
        this.refreshSelectionHighlight();
        this.updateCountdowns();
        this.cards[0]?.animateArrive();
      },
    });
  }

  shutdown(): void {
    this.unsubShop?.();
    this.unsubShop = undefined;
    this.unsubNewOffer?.();
    this.unsubNewOffer = undefined;
    for (const card of this.cards) card.destroy();
    this.cards = [];
  }

  // -- room / fixtures ------------------------------------------------------

  private buildRoom(): void {
    // Dim back wall (full screen).
    this.add.rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, PALETTE.shopWall, 1).setOrigin(0, 0);
    // Slightly lighter wall band at the top for the banner.
    this.add.rectangle(0, 0, NATIVE_WIDTH, 24, PALETTE.shopWallHi, 1).setOrigin(0, 0);
    // Lantern dots — three small accent rectangles.
    for (let i = 0; i < 3; i++) {
      const x = 60 + i * 100;
      this.add.rectangle(x, 14, 4, 4, PALETTE.shopLantern, 1).setOrigin(0.5, 0.5);
      this.add.rectangle(x, 14, 10, 10, PALETTE.shopLanternGlow, 0.18).setOrigin(0.5, 0.5);
    }
    // MARKET banner.
    this.add
      .text(NATIVE_WIDTH / 2, 12, t("shop.title"), {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(PALETTE.parchment),
      })
      .setOrigin(0.5, 0.5);

    // Floor band.
    this.add.rectangle(0, FLOOR_Y, NATIVE_WIDTH, NATIVE_HEIGHT - FLOOR_Y, PALETTE.shopFloor, 1).setOrigin(0, 0);
    this.add.rectangle(0, FLOOR_Y, NATIVE_WIDTH, 1, PALETTE.shopFloorEdge, 1).setOrigin(0, 0);
    // Floor pattern: thin tile separators.
    for (let x = 16; x < NATIVE_WIDTH; x += 24) {
      this.add.rectangle(x, FLOOR_Y + 2, 1, NATIVE_HEIGHT - FLOOR_Y - 4, PALETTE.shopFloorEdge, 0.6).setOrigin(0, 0);
    }

    // Cyan UGC corner-mark.
    this.add.rectangle(4, 4, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);

    // Exit portal — uses the runner's "tower.up" texture (climbing-out
    // doorway with light at the bottom + shadow at the top) so the
    // market exit reads with the same visual vocabulary as the runner's
    // sub-level exit gates. The door + glow sit behind the hero (low
    // depth) so the hero walks IN FRONT of the doorway, same as in the
    // runner when stepping through a sub-level portal. Labels stay at
    // HUD depth so they remain legible.
    const portalY = FLOOR_Y;
    this.exitGlow = this.add
      .rectangle(EXIT_PORTAL_X, portalY - 30, 22, 34, UGC_CYAN, 0.35)
      .setOrigin(0.5, 0)
      .setDepth(0);
    this.add
      .image(EXIT_PORTAL_X, portalY, "tower.up")
      .setOrigin(0.5, 1)
      .setScale(0.5)
      .setDepth(0);
    this.add
      .text(EXIT_PORTAL_X, portalY - 34, t("shop.exit"), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(PALETTE.parchment),
      })
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    this.add
      .text(EXIT_PORTAL_X + 14, portalY - 42, t("shop.hint.exit"), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(UGC_CYAN),
      })
      .setOrigin(0, 0)
      .setDepth(DEPTH_HUD);
    this.add
      .text(NATIVE_WIDTH / 2, NATIVE_HEIGHT - 8, t("shop.hint.controls"), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH_HUD);
  }

  private spawnHero(): void {
    // Spawn the hero in front of the card stack so the "items behind, hero
    // in front" relationship is the first thing the player sees.
    this.hero = this.physics.add.sprite(NATIVE_WIDTH / 2, FLOOR_Y - 8, KNIGHT_IDLE_KEY, 0);
    this.hero.setOrigin(0.5, 1.0);
    this.hero.setDisplaySize(40, 40);
    this.hero.setCollideWorldBounds(true);
    this.hero.setDepth(DEPTH_HERO);
    const body = this.hero.body as Phaser.Physics.Arcade.Body;
    body.setSize(20, 36);
    body.setOffset(22, 14);
    this.hero.play(KNIGHT_IDLE_ANIM);

    // Floor as a static body.
    const floor = this.physics.add.staticImage(NATIVE_WIDTH / 2, FLOOR_Y + 1, KNIGHT_IDLE_KEY);
    floor.setVisible(false);
    floor.body.setSize(NATIVE_WIDTH * 2, 8);
    this.physics.add.collider(this.hero, floor);

    // World bounds keep the hero in the room.
    this.physics.world.setBounds(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT);
  }

  /**
   * Build the vertical platform stack inside the card list. Each row
   * consists of a card (background, DEPTH_CARDS) followed by a slim
   * platform (DEPTH_PLATFORMS) sitting in the gap below the card. The hero
   * (DEPTH_HERO) renders in front of both, so they read as walking on
   * shelves with the cards forming a backdrop.
   *
   * Geometry is tuned so single-jump arc (~57 px at GAMEPLAY.gravity) clears
   * one row at a time, and floor → bottom platform is comfortably reachable.
   */
  private buildTradeBoard(): void {
    this.platformGroup = this.physics.add.staticGroup();
    const slots = GAMEPLAY.shopBoardVisibleCards;
    const platformCenterX = CARD_STACK_X + CARD_STACK_WIDTH / 2;
    for (let i = 0; i < slots; i++) {
      // Card occupies y = rowTopY .. rowTopY + OFFER_CARD_HEIGHT.
      // Platform sits 4 px below the card (in the gap between this row's
      // card and the next row's card).
      const rowTopY = STACK_TOP_Y + i * ROW_HEIGHT;
      const platformTopY = rowTopY + OFFER_CARD_HEIGHT + 4;
      const plat = this.add
        .rectangle(platformCenterX, platformTopY, CARD_STACK_WIDTH, PLATFORM_HEIGHT, PALETTE.shopFloorEdge, 1)
        .setOrigin(0.5, 0)
        .setDepth(DEPTH_PLATFORMS);
      plat.setStrokeStyle(1, PALETTE.shopWallHi);
      this.physics.add.existing(plat, true);
      this.platformGroup.add(plat);
      this.platforms.push(plat);
    }

    // One-way collider: hero only lands when falling from above. The
    // drop-through window (set by holding Down while grounded) makes the
    // process callback return false so the hero falls clean through.
    this.physics.add.collider(
      this.hero,
      this.platformGroup,
      undefined,
      (heroObj, platObj) => {
        if (this.time.now < this.dropThroughUntilMs) return false;
        const hb = (heroObj as Phaser.Physics.Arcade.Sprite).body as Phaser.Physics.Arcade.Body | null;
        const ps = platObj as Phaser.GameObjects.Rectangle;
        if (!hb) return false;
        const platTop = ps.y;
        const heroBottom = hb.y + hb.height;
        return hb.velocity.y > 0 && heroBottom <= platTop + 4;
      },
      this
    );
  }

  /**
   * Vertical scroll indicator on the right side of the room. Shows the
   * shape of the 20-offer buffer relative to the 4 currently visible: a
   * track (the full buffer), a cursor (the visible window), and up/down
   * triangles that dim when the corresponding direction has nowhere to go.
   * Sits at depth DEPTH_HUD so cards never obscure it.
   */
  private buildScrollIndicator(): void {
    const x = NATIVE_WIDTH - 16;
    const trackTop = STACK_TOP_Y;
    const trackBottom = STACK_TOP_Y + GAMEPLAY.shopBoardVisibleCards * ROW_HEIGHT;
    const trackHeight = trackBottom - trackTop;

    this.scrollTrack = this.add
      .rectangle(x, trackTop, 2, trackHeight, PALETTE.shopWallHi, 0.7)
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    this.scrollTrack.setStrokeStyle(1, PALETTE.shopFloorEdge);

    // Up triangle (apex up).
    this.scrollUpArrow = this.add
      .triangle(x, trackTop - 6, 0, -3, -4, 3, 4, 3, PALETTE.parchment)
      .setDepth(DEPTH_HUD);

    // Down triangle (apex down).
    this.scrollDownArrow = this.add
      .triangle(x, trackBottom + 6, 0, 3, -4, -3, 4, -3, PALETTE.parchment)
      .setDepth(DEPTH_HUD);

    // Cursor knob — sized proportionally to the visible window. Width 6.
    this.scrollCursor = this.add
      .rectangle(x, trackTop, 6, 12, UGC_CYAN, 1)
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD + 1);
    this.scrollCursor.setStrokeStyle(1, PALETTE.parchmentInk);

    // Numeric label BELOW the down arrow so it doesn't collide with the
    // shared HUD on the top row. Reads "1–3 / 20" style.
    this.scrollLabel = this.add
      .text(x, trackBottom + 14, "", {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(PALETTE.parchment),
      })
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);

    // Out-of-view arrival hint — sits to the LEFT of the up arrow, flashes
    // cyan when a new offer arrives while the player is scrolled away
    // from the top of the buffer. Starts invisible; tween manages alpha.
    this.newOfferHint = this.add
      .text(x - 8, trackTop - 6, t("shop.scroll.new"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(1, 0.5)
      .setDepth(DEPTH_HUD)
      .setAlpha(0);
  }

  /** Flash the "NEW ▲" hint if the freshly arrived offer is not currently
   *  visible (the player is scrolled past it). Pulses 3× then fades. */
  private maybeFlashOutOfViewHint(): void {
    if (this.scrollOffset === 0) return;
    if (!this.newOfferHint) return;
    this.tweens.killTweensOf(this.newOfferHint);
    this.newOfferHint.setAlpha(0);
    this.tweens.add({
      targets: this.newOfferHint,
      alpha: { from: 0, to: 1 },
      duration: 200,
      yoyo: true,
      repeat: 2,
      ease: "Sine.easeInOut",
    });
  }

  /**
   * Fictive "N players online" readout in the gap between the right edge
   * of the card stack and the scroll indicator. Random-walks ±1 every
   * ~10–20 s, clamped to [71, 77] so the number always feels alive but
   * roughly stable around the starting value.
   */
  private buildPlayersOnline(): void {
    // Pick a stable initial value within the band so different sessions
    // don't all open at 74.
    this.playersOnline = 71 + Math.floor(Math.random() * 7);
    // Strip lives between the right edge of the card stack and the scroll
    // track. Center the text in that gap.
    const stripCenterX = (CARD_STACK_X + CARD_STACK_WIDTH + (NATIVE_WIDTH - 18)) / 2;
    this.playersOnlineText = this.add
      .text(stripCenterX, 36, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.parchment),
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    this.refreshPlayersOnlineText();
    this.schedulePlayersOnlineDrift();
  }

  private refreshPlayersOnlineText(): void {
    this.playersOnlineText.setText(`${this.playersOnline}\nplayers\nonline`);
  }

  private schedulePlayersOnlineDrift(): void {
    const delayMs = 10_000 + Math.floor(Math.random() * 10_000); // 10–20 s
    this.time.delayedCall(delayMs, () => {
      // ±1 random walk clamped to [71, 77]. Subtle alpha pulse so the
      // change reads as motion, not a frame skip.
      const step = Math.random() < 0.5 ? -1 : 1;
      this.playersOnline = Phaser.Math.Clamp(this.playersOnline + step, 71, 77);
      this.refreshPlayersOnlineText();
      this.tweens.add({
        targets: this.playersOnlineText,
        alpha: { from: 0.4, to: 1 },
        duration: 320,
        ease: "Sine.easeOut",
      });
      this.schedulePlayersOnlineDrift();
    });
  }

  private updateScrollIndicator(): void {
    if (!this.scrollTrack) return;
    const total = this.shop.offers.length;
    const visible = Math.min(GAMEPLAY.shopBoardVisibleCards, total);
    const maxOffset = Math.max(0, total - GAMEPLAY.shopBoardVisibleCards);
    const trackTop = this.scrollTrack.y;
    const trackHeight = this.scrollTrack.height;
    // Cursor height proportional to visible / total. Never smaller than 8 px.
    const cursorHeight = total === 0
      ? trackHeight
      : Math.max(8, Math.floor((visible / total) * trackHeight));
    const ratio = maxOffset === 0 ? 0 : this.scrollOffset / maxOffset;
    const cursorY = trackTop + ratio * (trackHeight - cursorHeight);
    this.scrollCursor.height = cursorHeight;
    this.scrollCursor.setSize(this.scrollCursor.width, cursorHeight);
    this.scrollCursor.y = cursorY;
    // Dim arrows that aren't actionable.
    this.scrollUpArrow.setAlpha(this.scrollOffset > 0 ? 1 : 0.35);
    this.scrollDownArrow.setAlpha(this.scrollOffset < maxOffset ? 1 : 0.35);
    // Label: "1-4 / 20" — uses 1-based indexing so it reads naturally.
    if (total === 0) {
      this.scrollLabel.setText("0 / 0");
    } else {
      const from = this.scrollOffset + 1;
      const to = Math.min(total, this.scrollOffset + GAMEPLAY.shopBoardVisibleCards);
      this.scrollLabel.setText(`${from}–${to} / ${total}`);
    }
  }

  // -- wallet HUD ----------------------------------------------------------

  /**
   * Same WalletHud component the runner uses. Tooltips are skipped (the
   * market doesn't have the pointer-hover model), but the visual layout is
   * pixel-identical: HP rail + geo counter + skills strip + boot slot +
   * inventory slots in the exact same positions.
   */
  private buildWalletHud(): void {
    this.walletHud = new WalletHud(this);
    this.walletHud.setDepth(DEPTH_HUD);
  }

  private updateWalletHud(): void {
    if (!this.walletHud) return;
    this.walletHud.update({
      hp: this.wallet.hp,
      geo: this.wallet.geo,
      inventory: this.wallet.inventory,
      // The market only knows about tradeable skills (doubleJump,
      // escapeFeather); jump/attack/interact are always-on capabilities so
      // they render as owned. EscapeFeather doesn't have its own HUD icon
      // — feather charges show in the bottom rail in the runner; here the
      // player can still see them via the icons in the offer cards.
      skills: {
        jump: true,
        doubleJump: this.wallet.isSkillUnlocked("doubleJump"),
        attack: true,
        interact: true,
      },
      bootBoostActive: false,
    });
  }

  // -- action prompt -------------------------------------------------------

  /**
   * Builds the ambient "[Y] TRADE" / "INSUFFICIENT …" prompt that floats
   * above the hero whenever they're standing on a platform. Hidden when
   * the hero is on the floor / in the air.
   */
  private buildActionPrompt(): void {
    this.actionPromptBg = this.add
      .rectangle(0, 0, 90, 14, PALETTE.shopWall, 0.92)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, UGC_CYAN);
    this.actionPromptText = this.add
      .text(0, 0, "", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.actionPrompt = this.add
      .container(0, 0, [this.actionPromptBg, this.actionPromptText])
      .setDepth(DEPTH_HUD + 2)
      .setVisible(false);
  }

  private updateActionPrompt(): void {
    const sel = this.selectedOfferIndex();
    if (sel < 0) {
      this.actionPrompt.setVisible(false);
      return;
    }
    const offer = this.shop.offers[sel];
    if (!offer) {
      this.actionPrompt.setVisible(false);
      return;
    }
    const costDelta = resourcesToDelta(offer.cost);
    const receiveDelta = resourcesToDelta(offer.receive);
    const affordable = offerIsAffordable(this.wallet, costDelta, receiveDelta);
    if (affordable) {
      this.actionPromptText.setText(t("shop.action.trade"));
      this.actionPromptText.setColor(this.toHex(UGC_CYAN));
      this.actionPromptBg.setStrokeStyle(1, UGC_CYAN);
    } else {
      this.actionPromptText.setText(this.whyUnaffordable(offer));
      this.actionPromptText.setColor(this.toHex(0xff8080));
      this.actionPromptBg.setStrokeStyle(1, 0xff8080);
    }
    // Resize bg to fit the longest text we'd render.
    const w = Math.max(70, this.actionPromptText.width + 12);
    this.actionPromptBg.setSize(w, 14);
    this.actionPrompt.setPosition(this.hero.x, this.hero.y - 52);
    this.actionPrompt.setVisible(true);
  }

  private whyUnaffordable(offer: TradeOffer): string {
    const cost = resourcesToDelta(offer.cost);
    if (cost.geo !== undefined && this.wallet.geo < cost.geo) {
      return t("shop.need.geo", { n: cost.geo - this.wallet.geo });
    }
    if (cost.hp !== undefined && this.wallet.hp < cost.hp) {
      return t("shop.need.hp", { n: cost.hp - this.wallet.hp });
    }
    if (cost.featherCharges !== undefined && this.wallet.featherCharges < cost.featherCharges) {
      return t("shop.need.feather", { n: cost.featherCharges - this.wallet.featherCharges });
    }
    if (cost.inventory?.remove) {
      for (const itemId of cost.inventory.remove) {
        if (!this.wallet.inventory.some((it) => it.kind === itemId)) {
          return t("shop.need.item", { item: itemId.toUpperCase() });
        }
      }
    }
    if (cost.skills?.lose) {
      for (const id of cost.skills.lose) {
        if (!this.wallet.isSkillUnlocked(id)) {
          const skill = id === "doubleJump"
            ? t("shop.skill.short.double_jump")
            : t("shop.skill.short.feather");
          return t("shop.missing.skill", { skill });
        }
      }
    }
    return t("shop.action.insufficient");
  }

  // -- cards ---------------------------------------------------------------

  private renderCards(): void {
    // Tear down existing cards.
    for (const c of this.cards) c.destroy();
    this.cards = [];

    const offers = this.shop.offers;
    // Clamp scrollOffset.
    const max = Math.max(0, offers.length - GAMEPLAY.shopBoardVisibleCards);
    if (this.scrollOffset > max) this.scrollOffset = max;

    // Drop any tooltip pointing at a card we're about to destroy — its
    // anchor coordinates would be stale, and the destroyed card no longer
    // emits pointerout to clean up on its own.
    ProfileTooltipService.get(this.game)?.hide("forced");

    for (let i = 0; i < GAMEPLAY.shopBoardVisibleCards; i++) {
      const offer = offers[this.scrollOffset + i];
      if (!offer) continue;
      // Card sits directly above its row's platform; cards are in the
      // backdrop layer (DEPTH_CARDS), platform in DEPTH_PLATFORMS, hero on
      // top in DEPTH_HERO so the hero reads as walking in front.
      const cardY = STACK_TOP_Y + i * ROW_HEIGHT;
      const identity = this.identity.forSeed(offer.authorAvatarSeed);
      const card = new OfferCard(this, CARD_STACK_X, cardY, offer, identity, this.identity);
      card.container.setDepth(DEPTH_CARDS);
      card.setTier(this.tierFor(offer));
      this.attachProfileHover(card, offer.authorAvatarSeed, CARD_STACK_X, cardY);
      this.cards.push(card);
    }
    // Reset selection highlight after re-render.
    this.refreshSelectionHighlight();
    this.updateScrollIndicator();
  }

  /**
   * Make the card hoverable for the profile tooltip. The hit zone covers the
   * full 200×36 card so the player doesn't have to aim at the tiny avatar
   * to discover the affordance. 200 ms hover delay (built into the service)
   * keeps casual cursor movement from flickering tooltips open.
   */
  private attachProfileHover(card: OfferCard, authorSeed: number, x: number, y: number): void {
    const profileSvc = ProfileTooltipService.get(this.game);
    if (!profileSvc) return;
    const container = card.container;
    container.setSize(OFFER_CARD_WIDTH, OFFER_CARD_HEIGHT);
    container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, OFFER_CARD_WIDTH, OFFER_CARD_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    container.on("pointerover", () => {
      profileSvc.show(authorSeed, {
        source: "shop",
        trigger: "hover",
        anchor: { x, y, width: OFFER_CARD_WIDTH, height: OFFER_CARD_HEIGHT },
      });
    });
    container.on("pointerout", () => {
      profileSvc.cancelPending();
      profileSvc.hide("mouse_leave");
    });
  }

  /**
   * Selected offer index in the global buffer = scrollOffset + platformIdx,
   * where platformIdx is the platform the hero is currently standing on.
   * Returns -1 if the hero is in the air or on the floor.
   */
  private selectedOfferIndex(): number {
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (!body) return -1;
    if (!body.blocked.down) return -1;
    const heroBottom = body.y + body.height;
    for (let i = 0; i < this.platforms.length; i++) {
      const plat = this.platforms[i]!;
      const platTop = plat.y;
      const overPlatformX =
        this.hero.x >= plat.x - CARD_STACK_WIDTH / 2 - 2 &&
        this.hero.x <= plat.x + CARD_STACK_WIDTH / 2 + 2;
      if (overPlatformX && Math.abs(heroBottom - platTop) < 4) {
        return this.scrollOffset + i;
      }
    }
    return -1;
  }

  private updateCountdowns(): void {
    const now = this.time.now;
    for (let i = 0; i < this.cards.length; i++) {
      const offer = this.shop.offers[this.scrollOffset + i];
      if (!offer) continue;
      const remaining = OFFER_LIFETIME_MS - (now - offer.arrivedAtMs);
      this.cards[i]!.setCountdown(remaining);
    }
  }

  private refreshSelectionHighlight(): void {
    const sel = this.selectedOfferIndex();
    for (let i = 0; i < this.cards.length; i++) {
      this.cards[i]?.setHighlighted(this.scrollOffset + i === sel);
    }
    // Glow the platform the hero is on.
    for (let i = 0; i < this.platforms.length; i++) {
      const plat = this.platforms[i]!;
      plat.fillColor = this.scrollOffset + i === sel ? UGC_CYAN : PALETTE.shopFloorEdge;
    }
  }

  private tryAcceptStandingOffer(): void {
    const sel = this.selectedOfferIndex();
    if (sel < 0) {
      this.flashHintBanner(t("shop.hint.stand_on_platform"));
      this.audio.shopReject();
      return;
    }
    const offers = this.shop.offers;
    const offer = offers[sel];
    if (!offer) return;
    const costDelta = resourcesToDelta(offer.cost);
    const receiveDelta = resourcesToDelta(offer.receive);
    const affordable = offerIsAffordable(this.wallet, costDelta, receiveDelta);
    this.telemetry.emit(1, "ugc.shop.offer.accept.attempt", {
      offerId: offer.id,
      templateId: offer.templateId,
      affordable,
    });
    const platformIdx = sel - this.scrollOffset;
    const card = this.cards[platformIdx];
    if (affordable) {
      // Resources fly to/from the place where the offer's icons live (the
      // card the player accepted), not a generic bottom-center "register".
      const cardCenter = card
        ? {
            x: card.container.x + 78, // card width = 200, anchor on cost-side
            y: card.container.y + 24,
          }
        : { x: NATIVE_WIDTH / 2, y: FLOOR_Y - 12 };
      const receiveCenter = card
        ? {
            x: card.container.x + 150, // receive-side icons sit further right
            y: card.container.y + 24,
          }
        : cardCenter;
      this.animateResourcesFly(offer.cost, "out", cardCenter);
      this.shop.acceptOffer(offer.id, this.wallet);
      this.audio.shopTrade();
      this.time.delayedCall(160, () => this.animateResourcesFly(offer.receive, "in", receiveCenter));
      card?.tweenTo((card.container.x ?? 0) + 240, card.container.y, 220, () => card?.destroy());
      this.time.delayedCall(240, () => this.renderCards());
    } else {
      this.audio.shopReject();
      card?.flashRejected();
    }
  }

  /**
   * Spawn a flying icon per resource. "out" = HUD → bottom-center (the
   * register / counter); "in" = bottom-center → HUD. Used right after a
   * successful trade so the wallet change is visualised, not just numeric.
   */
  private animateResourcesFly(
    resources: readonly TradeOffer["cost"][number][],
    direction: "in" | "out",
    register: { x: number; y: number },
  ): void {
    let staggerMs = 0;
    for (const r of resources) {
      this.time.delayedCall(staggerMs, () => this.flyOne(r, direction, register));
      staggerMs += 80;
    }
  }

  private flyOne(
    r: TradeOffer["cost"][number],
    direction: "in" | "out",
    register: { x: number; y: number },
  ): void {
    const anchor = this.hudAnchorForResource(r);
    if (!anchor) return;
    const start = direction === "out" ? anchor : register;
    const end = direction === "out" ? register : anchor;
    const icon = this.add
      .image(start.x, start.y, this.iconKeyForResource(r))
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD + 5)
      .setScale(1.4);
    this.tweens.add({
      targets: icon,
      x: end.x,
      y: end.y,
      scale: direction === "out" ? 0.6 : 1.0,
      alpha: direction === "out" ? 0.2 : 1,
      duration: 380,
      ease: direction === "out" ? "Cubic.easeIn" : "Cubic.easeOut",
      onComplete: () => icon.destroy(),
    });
  }

  private hudAnchorForResource(r: TradeOffer["cost"][number]): { x: number; y: number } | undefined {
    switch (r.kind) {
      case "geo":
        return this.walletHud.geoAnchor();
      case "hp":
        return this.walletHud.hpAnchor(Math.max(0, this.wallet.hp - 1));
      case "feather":
        return this.walletHud.bootAnchor();
      case "inventoryItem": {
        const idx = Math.max(0, this.wallet.inventory.length - 1);
        return this.walletHud.inventoryAnchor(idx);
      }
      case "skill":
        return this.walletHud.skillAnchor(r.skillId === "doubleJump" ? "doubleJump" : "doubleJump");
    }
  }

  private iconKeyForResource(r: TradeOffer["cost"][number]): string {
    switch (r.kind) {
      case "geo":
        return "shopIcon.geo";
      case "hp":
        return "shopIcon.hp";
      case "feather":
        return "shopIcon.feather";
      case "inventoryItem":
        return "shopIcon.scroll";
      case "skill":
        return r.skillId === "doubleJump" ? "shopIcon.skillDoubleJump" : "shopIcon.skillEscapeFeather";
    }
  }

  private flashHintBanner(text: string): void {
    const t = this.add
      .text(NATIVE_WIDTH / 2, 32, text, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.bannerText),
      })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD);
    this.tweens.add({
      targets: t,
      alpha: 0,
      duration: 800,
      delay: 400,
      ease: "Quad.easeOut",
      onComplete: () => t.destroy(),
    });
  }

  private tierFor(offer: TradeOffer): OfferAffordabilityTier {
    const cost = resourcesToDelta(offer.cost);
    if (this.wallet.canAfford(cost)) return "own";
    // Projected: combine wallet + forecast and re-check.
    const forecast = this.wallet.forecastDelta(GAMEPLAY.shopForecastSeconds);
    const projectedWallet: Wallet = {
      geo: this.wallet.geo + (forecast.geo ?? 0),
      hp: this.wallet.hp + (forecast.hp ?? 0),
      featherCharges: this.wallet.featherCharges + (forecast.featherCharges ?? 0),
      inventory: this.wallet.inventory,
      isSkillUnlocked: (id) => this.wallet.isSkillUnlocked(id),
      canAfford: () => false,
      apply: () => undefined,
      forecastDelta: () => ({}),
    };
    if (projectedWallet.geo >= (cost.geo ?? 0) && projectedWallet.hp >= (cost.hp ?? 0) && projectedWallet.featherCharges >= (cost.featherCharges ?? 0)) {
      return "projected";
    }
    return "out";
  }

  private exit(): void {
    const dwellMs = this.time.now - this.enteredAtMs;
    this.telemetry.emit(1, "ugc.shop.exit", { returnTo: this.returnTo, dwellMs });
    if (this.returnTo === "runner") {
      this.scene.stop();
      this.scene.resume("runner");
    } else {
      this.scene.start("launcher");
    }
  }

  private resolveWallet(): Wallet {
    if (this.walletSource === "runner") {
      const runnerWallet = this.registry.get("runnerWallet") as Wallet | undefined;
      if (runnerWallet) return runnerWallet;
      // Fallback: launcher snapshot, so the scene never crashes.
    }
    let snapshot = this.registry.get("launcherWalletSnapshot") as WalletSnapshot | undefined;
    if (!snapshot) {
      snapshot = makeDefaultWalletSnapshot();
      // Seed some currency so launcher trades are interactive in v1.
      snapshot.geo = 50;
      snapshot.skills.doubleJump = true;
      this.registry.set("launcherWalletSnapshot", snapshot);
    }
    return new LauncherWallet(snapshot);
  }

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}

// Suppress no-unused-vars / no-explicit-any helper.
export type _ShopSceneUnused = AuthorBadge | AuthorIdentity;
